import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import UsuariosService from "../../service/usuario";
import Menu from "../menu/Menu";
import Cabecalho from "../cabecalho/Cabecalho";
import Rodape from "../rodape/Rodape";
import Titulo from "../titulo/Titulo";
import CampoPesquisa from "../pesquisa/CampoPesquisa";

const PERFIL_LABELS = {
  admin: "Admin",
  publicador: "Publicador",
  colaborador: "Colaborador",
  secretaria: "Secretária",
  "coordenador-geral": "Coordenador Geral",
  "coordenador-ponto": "Coordenador de Ponto",
  "coordenador-regional": "Coordenador Regional",
};

const formatTelefone = (valor) => {
  if (!valor) return "";
  const digits = String(valor).replace(/\D/g, "");
  if (digits.length === 11)
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  if (digits.length === 10)
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  return valor;
};

export default function ConsultarPublicadores() {
  const [query, setQuery] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [circuitoFiltro, setCircuitoFiltro] = useState("todos");
  const [publicadores, setPublicadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPublicadores = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await UsuariosService.listar({ page: 1, limit: 9999 });
      const lista = Array.isArray(data?.usuarios) ? data.usuarios : [];
      setPublicadores(lista);
    } catch (err) {
      setError(err.message || "Erro ao carregar publicadores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await loadPublicadores();
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const normalizeStr = (str) =>
    str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const toStr = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) return val.join(" ");
    if (typeof val === "object") return val.nome || "";
    return String(val);
  };

  const circuitos = useMemo(() => {
    const set = new Set();
    publicadores.forEach((p) => {
      const c = p.congregacao?.circuito || p.circuito;
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [publicadores]);

  const getCircuito = (p) =>
    p.congregacao?.circuito || p.circuito || "";

  const filtered = useMemo(() => {
    const term = query.trim();
    const termNorm = term ? normalizeStr(term) : "";

    return publicadores.filter((p) => {
      const isAtivo = p.isSenhaTemporaria === false;

      // filtro do dropdown status
      if (statusFiltro === "ativo" && !isAtivo) return false;
      if (statusFiltro === "inativo" && isAtivo) return false;

      // filtro do dropdown circuito
      if (circuitoFiltro !== "todos" && getCircuito(p) !== circuitoFiltro) return false;

      // filtro do campo de texto
      if (!termNorm) return true;

      const nome = normalizeStr(toStr(p.nome));
      const email = normalizeStr(toStr(p.email));
      const telefone = normalizeStr(toStr(p.telefone));
      const congregacao = normalizeStr(toStr(p.congregacao));
      const circuito    = normalizeStr(getCircuito(p));

      const perfis = Array.isArray(p.perfil) ? p.perfil : p.perfil ? [p.perfil] : [];
      const perfilMatch = perfis.some((pf) => {
        const rawNorm = normalizeStr(pf);
        const labelNorm = normalizeStr(PERFIL_LABELS[pf] || pf);
        return rawNorm.includes(termNorm) || labelNorm.includes(termNorm);
      });

      return (
        nome.includes(termNorm) ||
        email.includes(termNorm) ||
        telefone.includes(termNorm) ||
        congregacao.includes(termNorm) ||
        circuito.includes(termNorm) ||
        perfilMatch
      );
    });
  }, [query, statusFiltro, circuitoFiltro, publicadores]);

  return (
    <PageWrapper>
      <Menu />
      <Cabecalho />
      <MainContent>
        <Container>
          <HeaderRow>
            <div>
              <Titulo>Publicadores</Titulo>
            </div>
            <FiltrosWrapper>
              <StatusSelect
                value={circuitoFiltro}
                onChange={(e) => setCircuitoFiltro(e.target.value)}
              >
                <option value="todos">Todos os circuitos</option>
                {circuitos.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </StatusSelect>
              <StatusSelect
                value={statusFiltro}
                onChange={(e) => setStatusFiltro(e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </StatusSelect>
              <SearchWrapper>
                <CampoPesquisa
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Pesquisar"
                />
              </SearchWrapper>
            </FiltrosWrapper>
          </HeaderRow>

          {loading && <MensagemLoading>Carregando...</MensagemLoading>}
          {error && <MensagemErro>{error}</MensagemErro>}

          <TabelaContainer>
            <TabelaUsuarios>
              <thead>
                <LinhaCabecalho>
                  <CelulaCabecalho>Nome</CelulaCabecalho>
                  <CelulaCabecalho>E-mail</CelulaCabecalho>
                  <CelulaCabecalho>Telefone</CelulaCabecalho>
                  <CelulaCabecalho>Congregação</CelulaCabecalho>
                  <CelulaCabecalho>Circuito</CelulaCabecalho>
                  <CelulaCabecalho>Perfil</CelulaCabecalho>
                  <CelulaCabecalho>Status</CelulaCabecalho>
                </LinhaCabecalho>
              </thead>
              <tbody>
                {!loading && filtered.length === 0 && (
                  <LinhaVazia>
                    <CelulaVazia colSpan="7">
                      Nenhum publicador encontrado
                    </CelulaVazia>
                  </LinhaVazia>
                )}
                {filtered.map((p) => (
                  <LinhaUsuario key={p._id || p.id}>
                    <CelulaUsuario>{p.nome || ""}</CelulaUsuario>
                    <CelulaUsuario>{p.email || ""}</CelulaUsuario>
                    <CelulaUsuario style={{ whiteSpace: "nowrap" }}>{formatTelefone(p.telefone)}</CelulaUsuario>
                    <CelulaUsuario>
                      {p.congregacao
                        ? typeof p.congregacao === "string"
                          ? p.congregacao
                          : p.congregacao.nome || ""
                        : ""}
                    </CelulaUsuario>
                    <CelulaUsuario>{getCircuito(p)}</CelulaUsuario>
                    <CelulaPerfilUsuario>
                      <PerfilList>
                        {(Array.isArray(p.perfil) ? p.perfil : p.perfil ? [p.perfil] : []).map((pf, i) => (
                          <PerfilBadge key={i}>{PERFIL_LABELS[pf] || pf}</PerfilBadge>
                        ))}
                      </PerfilList>
                    </CelulaPerfilUsuario>
                    <CelulaUsuario>
                      <StatusBadge ativo={p.isSenhaTemporaria === false}>
                        {p.isSenhaTemporaria === false ? "Ativo" : "Inativo"}
                      </StatusBadge>
                    </CelulaUsuario>
                  </LinhaUsuario>
                ))}
              </tbody>
            </TabelaUsuarios>
          </TabelaContainer>
        </Container>
      </MainContent>
      <Rodape />
    </PageWrapper>
  );
}

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const MainContent = styled.main`
  padding-top: 85px;
  padding-left: 260px;
  padding-right: 24px;
  padding-bottom: 60px;
  box-sizing: border-box;
  @media (max-width: 1200px) and (min-width: 768px) {
    padding-left: 80px;
  }
  @media (max-width: 767px) {
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 16px;
  }
`;

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: "Roboto", sans-serif;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
  padding-right: 24px;
  @media (max-width: 767px) {
    padding-right: 16px;
  }
`;

const SearchWrapper = styled.div`
  width: 260px;
  @media (max-width: 767px) {
    width: 100%;
  }
`;

const FiltrosWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  @media (max-width: 767px) {
    width: 100%;
    flex-direction: column;
  }
`;

const StatusSelect = styled.select`
  height: 44px;
  padding: 0 36px 0 15px;
  border: 1px solid #bdbdbd;
  border-radius: 24px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23555' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E") no-repeat right 14px center;
  appearance: none;
  font-family: "Roboto", sans-serif;
  font-size: 15px;
  color: #222;
  cursor: pointer;
  outline: none;
  min-width: 120px;
  &:focus {
    border-color: #999;
  }
  @media (max-width: 767px) {
    width: 100%;
  }
`;

const MensagemLoading = styled.div`
  padding: 12px;
  color: #666;
  font-style: italic;
`;

const MensagemErro = styled.div`
  padding: 12px 16px;
  background-color: #fee;
  color: #c33;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const TabelaContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-top: 8px;
`;

const TabelaUsuarios = styled.table`
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  font-size: 15px;
  table-layout: fixed;
`;

const LinhaCabecalho = styled.tr`
  background-color: #007c75;
`;

const CelulaCabecalho = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-weight: 400;
  color: #fff;
  border-bottom: 2px solid #007c75;
  font-size: 14px;
  letter-spacing: 0.5px;
  font-family: "Roboto", sans-serif;
  white-space: nowrap;
`;

const LinhaUsuario = styled.tr`
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
  &:hover {
    background-color: #f8f9fa;
  }
`;

const CelulaUsuario = styled.td`
  padding: 14px 16px;
  color: #333;
  font-size: 15px;
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  word-break: break-word;
  vertical-align: middle;
`;

const CelulaPerfilUsuario = styled.td`
  padding: 10px 16px;
  color: #333;
  font-size: 15px;
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  vertical-align: middle;
`;

const PerfilList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const PerfilBadge = styled.span`
  display: inline-block;
  padding: 2px 9px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  background-color: #e8f0fe;
  color: #1a56a0;
  margin: 2px 3px 2px 0;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${({ ativo }) => (ativo ? "#e6f4ea" : "#fce8e6")};
  color: ${({ ativo }) => (ativo ? "#1e7e34" : "#c0392b")};
`;

const LinhaVazia = styled.tr``;

const CelulaVazia = styled.td`
  text-align: center;
  padding: 40px 16px !important;
  color: #999;
  font-style: italic;
  font-family: "Roboto", sans-serif;
`;
